// Plain-language helpers for Bank Feeds: connection health, why a balance is
// off, and the next step for an account row. Pure functions; no tenant data.

const DAY = 24 * 60 * 60 * 1000;
export const STALE_DAYS = 3;

const STATUS = {
  login_required: { tone: 'bad', label: 'Bank login needed', detail: 'Use "Fix connection" to sign in to the bank again.' },
  revoked: { tone: 'bad', label: 'Access revoked at the bank', detail: 'Use "Fix connection" to grant access again.' },
  error: { tone: 'bad', label: 'Last sync failed', detail: 'Try Sync again; if it keeps failing use "Fix connection".' },
  pending_expiration: { tone: 'warn', label: 'Bank access expires soon', detail: 'Use "Fix connection" before it expires.' },
};

// One health line per connection: { tone: ok|warn|bad|muted, label, detail }.
export function syncHealth(item, now = Date.now()) {
  if (!item) return { tone: 'muted', label: 'Unknown', detail: '' };
  if (item.paused) return { tone: 'warn', label: 'Paused', detail: 'Syncing is paused for this connection.' };
  if (STATUS[item.status]) return STATUS[item.status];
  // A sync that was refused or failed after the last good one: say so, the
  // reason is shown on the bank's card (lastSyncLine).
  const last = item.last_sync_result;
  if (last && last.ok === false && (!item.last_synced_at || !last.at || new Date(last.at) > new Date(item.last_synced_at))) {
    return { tone: 'bad', label: item.last_synced_at ? 'Last sync did not run' : 'Sync did not run', detail: 'See the reason below.' };
  }
  if (!item.last_synced_at) return { tone: 'muted', label: 'Not synced yet', detail: 'Map each bank account, then press Sync.' };
  const days = Math.floor((now - new Date(item.last_synced_at).getTime()) / DAY);
  if (days >= STALE_DAYS) return { tone: 'warn', label: `No sync for ${days} days`, detail: 'Press Sync to bring in newer activity.' };
  return { tone: 'ok', label: 'Healthy', detail: '' };
}

const cents = (v) => Math.round(Number(v) * 100);
const usd = (c) => `${c < 0 ? '-' : ''}$${(Math.abs(c) / 100).toFixed(2)}`;

// Why the bank and eloNest balances differ, as a list of lines to check, most
// likely first. Setting the starting balance from the bank (a plug) is the
// last resort, offered separately.
export function gapBreakdown(pa) {
  const b = pa && pa.balance;
  if (!b || !b.mismatch || b.live_balance === null || b.ledger_balance === null) return null;
  const bank = cents(b.live_balance);
  const books = cents(b.ledger_balance);
  const diff = bank - books;
  const lines = [];
  if (pa.pending_count) lines.push(`${pa.pending_count} pending bank transaction${pa.pending_count === 1 ? '' : 's'} are not in either balance until they post.`);
  lines.push('Possible duplicates waiting for review above (a row counted twice moves the eloNest balance).');
  lines.push('Bank activity newer than the last sync: press Sync, then Refresh balances.');
  lines.push('Transactions dated before the first imported row, or a starting balance that was never set.');
  lines.push('Rows typed in or imported from a file on this account that the bank never had.');
  return {
    bank: usd(bank), elonest: usd(books), difference: usd(diff),
    direction: diff > 0 ? 'The bank shows more than eloNest.' : 'eloNest shows more than the bank.',
    lines
  };
}

// The last sync result for one bank: this session's (local) or the one the
// server kept (item.last_sync_result). { tone, text } or null.
export function lastSyncLine(item, local = null) {
  const r = local || (item && item.last_sync_result) || null;
  if (!r) return null;
  let text = r.message || '';
  if (!text && r.ok) {
    const n = Number(r.match_review) || 0;
    text = `${Number(r.added) || 0} new, ${Number(r.modified) || 0} updated, ${Number(r.removed) || 0} removed, ${n} possible duplicate${n === 1 ? '' : 's'} to review.`;
  }
  if (!text) return null;
  if (local && local.kind === 'action') return { tone: r.ok ? 'ok' : 'bad', text };
  const when = r.at ? new Date(r.at).toLocaleString() : '';
  const tone = r.ok ? (r.info ? 'muted' : 'ok') : (r.info ? 'warn' : 'bad');
  return { tone, text: `Last sync result${when ? ` (${when})` : ''}: ${text}` };
}

// The next step for a row that has no balance yet.
export function nextStep(item, pa) {
  if (!pa.account_id && !pa.ignored) return { kind: 'pick', text: 'Choose an eloNest account (or create one) in the list at left, or skip it.' };
  if (pa.ignored) return { kind: 'ignored', text: 'Set to "Don\'t import": this bank account\'s transactions and balance are skipped. Choose an eloNest account to start importing it.' };
  if (!item.last_synced_at) return { kind: 'sync', text: 'Mapped. Press Sync to bring in transactions.' };
  return null;
}
